/**
 * The only place that talks to the network.
 *
 * Requests are same-origin in both dev and production — Vite proxies /api to
 * the backend — so there is no base URL to configure and nothing to get wrong
 * between environments.
 */

import type { Attribution } from "../lib/attribution";
import type {
  Pricing,
  Quote,
  ActionResult,
  AdminOverview,
  AdminUserList,
  AuthConfig,
  AuthResponse,
  Building,
  CountiesResponse,
  CoverageResponse,
  ErrorCode,
  LegalDocument,
  LegalIndex,
  GuideIndex,
  LegalSlug,
  Paged,
  PlanChangePreview,
  PlanChangeResult,
  PlansResponse,
  RefreshRun,
  RefreshTrigger,
  ReportRecord,
  ReportsResponse,
  ScoringRules,
  Status,
  Unit,
  User,
  Watch,
  WatchFilters,
  WatchList,
  WatchMatches,
} from "./types";

const TOKEN_KEY = "ff_token";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  /** Extra fields the server attached, e.g. needs_tier or retry_after. */
  readonly extra: Record<string, unknown>;

  constructor(message: string, status: number, code: ErrorCode,
              extra: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.extra = extra;
  }

  /** The plan this request would need. Set on 402s. */
  get needsTier(): string | null {
    return typeof this.extra.needs_tier === "string" ? this.extra.needs_tier : null;
  }
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

let token: string | null = readToken();

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // Private mode, or storage disabled. Sessions just won't persist.
  }
}

export function setToken(next: string | null): void {
  token = next;
  try {
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* non-fatal, see readToken */
  }
}

export function getToken(): string | null {
  return token;
}

function authHeaders(): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Whether a rejection is "I cancelled this" rather than "this failed".
 *
 * One implementation, because it is the same distinction in two places that
 * mean different things by getting it wrong. `useRows` aborts in-flight
 * requests whenever the filters change, and reading that as a failure paints
 * an error over a table whose request was merely superseded. `AuthProvider`
 * aborts its `/api/auth/me` on cleanup, and reading *that* as a failure is
 * worse: it means treating a cancelled request as a refused credential, which
 * drops the token and signs the user out while their session is perfectly
 * good. It did exactly that under React's development double-invoke, and the
 * page it happened on was the one Stripe returns a buyer to.
 *
 * Non-errors are refusals, not aborts: a rejection with a string in it is
 * something going wrong, and the safe direction here is to report it.
 */
export function isAbort(cause: unknown): boolean {
  return cause instanceof DOMException && cause.name === "AbortError";
}

// ---------------------------------------------------------------------------
// Involuntary sign-out
// ---------------------------------------------------------------------------

/**
 * Why a session stopped working without the user asking it to.
 *
 * Two reasons, kept apart because they are different pieces of news: "expired"
 * is routine and self-inflicted, "superseded" says the account signed in
 * somewhere else. Collapsing them into one "please sign in again" would hide
 * the only signal a customer has that their credentials are being used by
 * someone else.
 */
export type SessionEnd = "superseded" | "expired";

let sessionEndListener: ((reason: SessionEnd) => void) | null = null;

/**
 * Register the one listener notified when the server refuses our token.
 *
 * One listener, not a set: AuthProvider is the only thing that can act on this,
 * and a second subscriber would mean two places deciding what the session is.
 */
export function setSessionEndListener(
  fn: ((reason: SessionEnd) => void) | null,
): void {
  sessionEndListener = fn;
}

function noteSessionEnd(error: ApiError): void {
  // `token` guards against announcing a dead session to a visitor who never
  // had one — a signed-out caller hitting a gated route gets `no_token`, which
  // is not news.
  if (error.status !== 401 || !token) return;

  const reason: SessionEnd | null =
    error.code === "session_superseded"
      ? "superseded"
      : error.code === "invalid_token"
        ? "expired"
        : null;
  if (!reason) return;

  // Drop the token before notifying. It is dead, and any request still carrying
  // it is a request that cannot succeed — including the ones already queued
  // behind this one.
  setToken(null);
  sessionEndListener?.(reason);
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

interface RequestOptions {
  // DELETE joined GET and POST when saved alerts arrived: a watch is the
  // one thing in this app a user can destroy.
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      signal,
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...authHeaders(),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (cause) {
    // AbortError is the caller cancelling; let it through untouched so the
    // caller can tell "I cancelled this" from "the network is down".
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError("Can't reach the server.", 0, "network_error");
  }

  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Turn a failed response into an ApiError.
 *
 * It also reports an involuntary sign-out, which makes it impure — but this is
 * the one funnel every non-ok response passes through, `download` included, and
 * a session check that a future caller can bypass by not using `request` is a
 * session check that will be bypassed.
 */
async function toError(response: Response): Promise<ApiError> {
  let detail = "Something went wrong.";
  let code: ErrorCode = "error";
  let extra: Record<string, unknown> = {};

  try {
    const body = (await response.json()) as Record<string, unknown>;
    if (typeof body.detail === "string") detail = body.detail;
    if (typeof body.code === "string") code = body.code;
    const { detail: _d, code: _c, ...rest } = body;
    extra = rest;
  } catch {
    /* a non-JSON error body; the defaults are already sensible */
  }

  const error = new ApiError(detail, response.status, code, extra);
  noteSessionEnd(error);
  return error;
}

/**
 * A GET whose body is not JSON, carrying the session token like everything else.
 *
 * Separate from `request` rather than a flag on it, because the two differ in
 * what they *return* and a boolean that changes a return type is how a caller
 * ends up parsing HTML as an object. Failures still go through `toError`, so
 * an ended session is noticed here as it is everywhere.
 */
async function authedFetch(path: string,
                           signal?: AbortSignal): Promise<Response> {
  try {
    return await fetch(path, { signal, headers: { ...authHeaders() } });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError("Can't reach the server.", 0, "network_error");
  }
}

/** The body as text. For the report preview, which is a document. */
async function text(path: string, signal?: AbortSignal): Promise<string> {
  const response = await authedFetch(path, signal);
  if (!response.ok) throw await toError(response);
  return response.text();
}

// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------

type QueryValue = string | number | boolean | null | undefined;

/**
 * Builds a query string, dropping anything empty. Sending `search=` or
 * `flag=` as blanks works, but it makes the URLs in the network tab and the
 * server logs much harder to read.
 */
export function query(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    if (value === false) continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface BuildingQuery {
  limit?: number;
  offset?: number;
  milestone_only?: boolean;
  unsafe_only?: boolean;
  min_score?: number;
  min_rate?: number;
  search?: string;
  order?: string;
  /** Five-digit county FIPS. Empty means every county we hold. */
  county?: string;
}

export interface UnitQuery {
  limit?: number;
  offset?: number;
  min_score?: number;
  flag?: string;
  search?: string;
  parent_folio?: string;
  condo_only?: boolean;
  county?: string;
}

export const api = {
  /**
   * `attribution` is where the visitor came from (lib/attribution.ts). Sent
   * only here and to `google`: it is written onto the account once, at
   * creation, and has no meaning on any other request.
   */
  signup: (
    email: string,
    password: string,
    acceptedAgreement: boolean,
    attribution?: Attribution,
  ) =>
    request<AuthResponse>("/api/auth/signup", {
      method: "POST",
      body: {
        email,
        password,
        accepted_agreement: acceptedAgreement,
        ...(attribution ? { attribution } : {}),
      },
    }),

  login: (email: string, password: string) =>
    request<AuthResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    }),

  me: (signal?: AbortSignal) => request<User>("/api/auth/me", { signal }),

  /**
   * Ask for a password reset link.
   *
   * Resolves the same way whether or not the address has an account — the
   * server will not say, and the client must not appear to know either. The
   * sentence to show comes back in `detail` rather than being written here, so
   * the page cannot promise something the server did not.
   */
  requestPasswordReset: (email: string) =>
    request<ActionResult>("/api/auth/password/forgot", {
      method: "POST",
      body: { email },
    }),

  /** Spend a reset link. Returns no token: a reset is not a sign-in. */
  resetPassword: (token: string, password: string) =>
    request<ActionResult>("/api/auth/password/reset", {
      method: "POST",
      body: { token, password },
    }),

  /** What the sign-in page needs before anyone types anything. */
  authConfig: (signal?: AbortSignal) =>
    request<AuthConfig>("/api/auth/config", { signal }),

  /** Exchange Google's ID token for ours. */
  google: (credential: string, attribution?: Attribution) =>
    request<AuthResponse>("/api/auth/google", {
      method: "POST",
      body: { credential, ...(attribution ? { attribution } : {}) },
    }),

  /** Set or move the county a County plan covers. Returns the updated user. */
  chooseCounty: (county: string) =>
    request<User>("/api/account/county", { method: "POST", body: { county } }),

  /** Freshness plus headline figures, scoped to a county when given one. */
  status: (county?: string, signal?: AbortSignal) =>
    request<Status>(`/api/status${query({ county })}`, { signal }),

  /** Every Florida county with its coverage status. Public — see /coverage. */
  counties: (signal?: AbortSignal) =>
    request<CountiesResponse>("/api/counties", { signal }),

  /** Municipalities inside one county, or every county when `county` is "". */
  coverage: (county?: string, signal?: AbortSignal) =>
    request<CoverageResponse>(`/api/coverage${query({ county })}`, { signal }),

  plans: (signal?: AbortSignal) =>
    request<PlansResponse>("/api/plans", { signal }),

  buildings: (params: BuildingQuery, signal?: AbortSignal) =>
    request<Paged<Building>>(`/api/buildings${query({ ...params })}`, { signal }),

  units: (params: UnitQuery, signal?: AbortSignal) =>
    request<Paged<Unit>>(`/api/units${query({ ...params })}`, { signal }),

  /** Saved alerts, each with how much it has to report. */
  watches: (signal?: AbortSignal) =>
    request<WatchList>("/api/watches", { signal }),

  createWatch: (body: {
    name: string;
    scope: "buildings" | "units";
    county_fips: string;
    filters: WatchFilters;
  }) => request<Watch>("/api/watches", { method: "POST", body }),

  /** The rows one alert has caught since it was last acknowledged. */
  watchMatches: (id: number, signal?: AbortSignal) =>
    request<WatchMatches>(`/api/watches/${id}`, { signal }),

  /** Moves the "new to you" mark to now. Explicit, never on open. */
  /**
   * Opt this account in or out of the weekly alert digest.
   *
   * Returns the whole refreshed user rather than the flag, so the caller
   * replaces its cached account with the server's answer instead of patching
   * a field into it — the same contract as `chooseCounty`.
   */
  setAlertEmail: (on: boolean) =>
    // Spelled out rather than composed with `query()`, which drops `false`
    // deliberately — an unset filter and a filter set to false mean the same
    // thing everywhere else in this file. Here they do not: `on=false` is the
    // opt-*out*, and omitting it makes the required parameter missing, so the
    // one direction that must always work would 422.
    request<User>(`/api/alerts/email?on=${on ? "true" : "false"}`, {
      method: "POST",
    }),

  ackWatch: (id: number) =>
    request<Watch>(`/api/watches/${id}/ack`, { method: "POST" }),

  deleteWatch: (id: number) =>
    request<void>(`/api/watches/${id}`, { method: "DELETE" }),

  legalIndex: (signal?: AbortSignal) =>
    request<LegalIndex>("/api/legal", { signal }),

  legalDocument: (slug: LegalSlug, signal?: AbortSignal) =>
    request<LegalDocument>(`/api/legal/${slug}`, { signal }),

  guideIndex: (signal?: AbortSignal) =>
    request<GuideIndex>("/api/guides", { signal }),

  guide: (slug: string, signal?: AbortSignal) =>
    request<LegalDocument>(`/api/guides/${slug}`, { signal }),

  /** Weights and labels, so a score can explain itself. Fetched once. */
  scoring: (signal?: AbortSignal) =>
    request<ScoringRules>("/api/scoring", { signal }),

  /** The report rate table. Public, and the only copy of these numbers. */
  pricing: (signal?: AbortSignal) =>
    request<Pricing>("/api/pricing", { signal }),

  /**
   * What a report over these filters would cost. Charges nothing.
   *
   * The filters go up and the *counts* come back priced — the client never
   * sends a count, because a client-supplied count is a client-supplied price.
   */
  quote: (
    params: Record<string, QueryValue>,
    signal?: AbortSignal,
  ) => request<Quote>(`/api/reports/quote${query(params)}`, { signal }),

  /**
   * The document as it will read, capped and labelled a preview.
   *
   * Returns HTML rather than rows, because the thing being previewed *is* a
   * document — asking the client to lay out rows would be a second description
   * of it that can promise what the PDF does not deliver.
   *
   * Fetched rather than framed directly. The session token rides in a header
   * and an iframe `src` cannot carry one, so the caller frames what this
   * returns. `text` rather than `request` for the same reason `download` needs
   * its own path: the body is not JSON.
   */
  reportPreview: (
    params: Record<string, QueryValue>,
    signal?: AbortSignal,
  ) => text(`/api/reports/preview${query(params)}`, signal),

  /** Every report this account has generated, newest first. */
  reports: (signal?: AbortSignal) =>
    request<ReportsResponse>("/api/reports", { signal }),

  /**
   * Build this report, render it, and keep it.
   *
   * A POST because it spends an allowance and writes something that cannot be
   * reconstructed afterwards — it must not be reachable by a link, a prefetch
   * or a reload. What comes back is the record, not the file; the file has its
   * own URL, which is what makes it re-downloadable at all.
   */
  generateReport: (params: Record<string, QueryValue>) =>
    request<ReportRecord>(`/api/reports${query(params)}`, { method: "POST" }),

  /**
   * A fresh payment link for a report already built and waiting.
   *
   * The way back from a checkout somebody cancelled or walked away from. It
   * builds nothing and prices nothing — the amount is the one already stored
   * on the row, so a rate change between the two attempts cannot restate what
   * this report costs. The link it returns is cut to what is left of the
   * report's own window, never a fresh twenty-four hours.
   */
  payForReport: (id: number) =>
    request<ReportRecord>(`/api/reports/${id}/checkout`, { method: "POST" }),

  /**
   * The stored document, as a Blob.
   *
   * Not an `<a href>`, because the session token is a header and a plain link
   * carries none — the download would arrive as a 401 rendered as a broken
   * PDF. `toError` still funnels the failure, so an ended session is noticed
   * here exactly as it is everywhere else.
   */
  reportPdf: async (id: number): Promise<Blob> => {
    const response = await authedFetch(`/api/reports/${id}/document.pdf`);
    if (!response.ok) throw await toError(response);
    return response.blob();
  },

  /**
   * Forget one report, document and all.
   *
   * Genuinely destructive in a way nothing else here is: every other row in
   * this product is rebuilt by the next county pull and this one cannot be.
   * The confirmation is the caller's job.
   */
  deleteReport: (id: number) =>
    request<void>(`/api/reports/${id}`, { method: "DELETE" }),

  /** Records acceptance of `version`; returns the refreshed user. */
  acceptAgreement: (version: string) =>
    request<User>("/api/legal/accept", { method: "POST", body: { version } }),

  checkout: (tier: string) =>
    request<{ url: string }>("/api/billing/checkout", {
      method: "POST",
      body: { tier },
    }),

  portal: () =>
    request<{ url: string }>("/api/billing/portal", { method: "POST" }),

  /** What switching to `tier` would cost. Charges nothing. */
  previewPlanChange: (tier: string) =>
    request<PlanChangePreview>("/api/billing/preview", {
      method: "POST",
      body: { tier },
    }),

  /** Moves the live subscription onto `tier`, prorated. Charges. */
  changePlan: (tier: string) =>
    request<PlanChangeResult>("/api/billing/change", {
      method: "POST",
      body: { tier },
    }),

  /* ------------------------------------------------------------------------
     Operator console. Every one of these is 403 `not_admin` for anybody else —
     the client hides the page, the server is what refuses it.
     ---------------------------------------------------------------------- */

  /** Per-county health, the account roster's shape, and the refresh config. */
  adminOverview: (signal?: AbortSignal) =>
    request<AdminOverview>("/api/admin/overview", { signal }),

  adminUsers: (limit = 100, signal?: AbortSignal) =>
    request<AdminUserList>(`/api/admin/users${query({ limit })}`, { signal }),

  /**
   * Start a pull. With `county` it pulls that one alone, which is the recovery
   * path when a county's rows are missing — the replacement is county-scoped,
   * so the others keep what they have.
   */
  adminRefresh: (county?: string, acceptDrop = false) =>
    request<RefreshTrigger>(
      `/api/admin/refresh${query({
        county,
        accept_drop: acceptDrop ? "true" : undefined,
      })}`,
      { method: "POST" },
    ),

  /** The refresh log: one row per county per run. */
  adminHistory: (county?: string, limit = 20, signal?: AbortSignal) =>
    request<{ runs: RefreshRun[] }>(
      `/api/admin/refresh/history${query({ county, limit })}`,
      { signal },
    ),

};
